import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Shield, UserCog, Plus, X, Lock } from 'lucide-react';
import { BrandedSpinner } from '@/components/common/BrandedLoading';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { userIdentityApi, roleApi } from '@/services/permissionsRolesApi';
import { LoadingState } from '@/components/common';

interface ProfileUserRolesCardProps {
  /** Profile email used to look up identity user. */
  email: string;
}

export const ProfileUserRolesCard: React.FC<ProfileUserRolesCardProps> = ({ email }) => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAdminAuth();
  const [selectedRoleToAdd, setSelectedRoleToAdd] = useState<string>('');

  const {
    data: identityUser,
    isLoading: isLoadingUser,
    error: userError,
  } = useQuery({
    queryKey: ['identity-user', email],
    queryFn: () => userIdentityApi.getByEmail(email),
    enabled: !!email,
    staleTime: 60 * 1000,
  });

  const { data: rolesResponse } = useQuery({
    queryKey: ['roles', 'active'],
    queryFn: () => roleApi.getActive({ size: 200 }),
    enabled: isSuperAdmin && !!identityUser,
    staleTime: 2 * 60 * 1000,
  });

  const updateRolesMutation = useMutation({
    mutationFn: ({ userId, roleNames }: { userId: string; roleNames: string[] }) =>
      userIdentityApi.updateRoles(userId, { roleNames }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['identity-user', email] });
      queryClient.invalidateQueries({ queryKey: ['roles', 'active'] });
      setSelectedRoleToAdd('');
    },
  });

  const allRoles = rolesResponse?.content ?? [];
  const currentRoleNames = identityUser?.roles ?? [];
  const availableToAdd = allRoles
    .map((r) => r.name)
    .filter((name) => !currentRoleNames.includes(name));

  const handleAddRole = () => {
    if (!identityUser?.id || !selectedRoleToAdd) return;
    const next = [...currentRoleNames, selectedRoleToAdd];
    updateRolesMutation.mutate({ userId: identityUser.id, roleNames: next });
  };

  const handleRemoveRole = (roleName: string) => {
    if (!identityUser?.id) return;
    const next = currentRoleNames.filter((r) => r !== roleName);
    updateRolesMutation.mutate({ userId: identityUser.id, roleNames: next });
  };

  if (isLoadingUser) {
    return <LoadingState message="Loading identity & roles..." />;
  }

  if (userError) {
    return (
      <Card className="border-dashed border-amber-500/50 bg-amber-500/5">
        <CardContent className="py-8 text-center">
          <Shield className="h-12 w-12 text-amber-600 dark:text-amber-400 mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground">
            Unable to load identity user for this profile. The user may not exist in the identity service yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!identityUser) {
    return (
      <Card className="overflow-hidden border-0 bg-gradient-to-br from-slate-500/10 via-slate-500/5 to-slate-500/10 dark:from-slate-500/20 dark:via-slate-500/10 dark:to-slate-500/20 shadow-lg">
        <CardContent className="p-8">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:gap-6">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-500 to-slate-600 text-white shadow-lg">
              <UserCog className="h-7 w-7" />
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-xl font-bold tracking-tight">Identity & roles</h2>
              <p className="text-sm text-muted-foreground mt-1">
                No identity user found for this email. Roles can be managed once the user exists in the identity service.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden border-0 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-amber-500/10 dark:from-violet-500/20 dark:via-fuchsia-500/10 dark:to-amber-500/20 shadow-lg">
        <CardContent className="p-8">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:gap-6">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-amber-500 text-white shadow-lg">
              <Shield className="h-7 w-7" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-xl font-bold tracking-tight">Identity & roles</h2>
              <p className="text-sm text-muted-foreground mt-1">
                View and manage this user&apos;s roles in the identity service. Only super admins can add or remove roles.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserCog className="h-5 w-5 text-violet-600 dark:text-violet-400" />
            Assigned roles
          </CardTitle>
          <CardDescription>
            {isSuperAdmin
              ? 'Add or remove roles below. Changes take effect immediately.'
              : 'Only super admins can update user roles. You can view the current roles.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {currentRoleNames.length === 0 ? (
              <p className="text-sm text-muted-foreground">No roles assigned.</p>
            ) : (
              currentRoleNames.map((roleName) => (
                <Badge
                  key={roleName}
                  variant="secondary"
                  className="pl-3 pr-1 py-1.5 text-sm font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200 border border-violet-200 dark:border-violet-800"
                >
                  {roleName}
                  {isSuperAdmin && (
                    <button
                      type="button"
                      onClick={() => handleRemoveRole(roleName)}
                      disabled={updateRolesMutation.isPending}
                      className="ml-1.5 rounded-full p-0.5 hover:bg-violet-200 dark:hover:bg-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50"
                      aria-label={`Remove role ${roleName}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </Badge>
              ))
            )}
          </div>

          {isSuperAdmin && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
              <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground">Super admin only</span>
              <div className="flex-1 min-w-0" />
              <Select
                value={selectedRoleToAdd}
                onValueChange={setSelectedRoleToAdd}
                disabled={updateRolesMutation.isPending || availableToAdd.length === 0}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Add role…" />
                </SelectTrigger>
                <SelectContent>
                  {availableToAdd.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={handleAddRole}
                disabled={!selectedRoleToAdd || updateRolesMutation.isPending}
                className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
              >
                {updateRolesMutation.isPending ? (
                  <BrandedSpinner />
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
